// SFU control-plane adapter — WebSocket only.
//
// The persistent SfuSocket is the ONE control plane between client and SFU node:
//   - request/response RPC (createTransport, produce, consume, pause, …)
//   - server→client push (newConsumer, producerPaused/Resumed/Closed)
//
// There is deliberately no REST/client-pull fallback and no polling: the SFU
// drives consumption by pushing `newConsumer`, and a control-socket reconnect
// re-syncs via `readyToConsume` (see useSfuJoin). The old REST adapter + the
// `VITE_SFU_WS` flag were removed — the shared REST routes still exist on the
// node for the mobile/portal/H.323 clients, but the web client no longer uses
// them.

import type { SfuSocket } from './sfu-socket'

export interface SfuControl {
  createTransport(direction: 'send' | 'recv'): Promise<any>
  connectTransport(transportId: string, dtlsParameters: unknown): Promise<void>
  produce(transportId: string, kind: string, rtpParameters: unknown, appData: unknown): Promise<{ producerId: string }>
  restartIce(transportId: string): Promise<{ iceParameters: unknown }>
  /** Ask for a stream the server is not currently sending us. `rtpCapabilities`
   *  is optional — the server reuses the ones `readyToConsume` already gave it. */
  consume(producerId: string, rtpCapabilities?: unknown): Promise<any>
  resumeConsumer(consumerId: string): Promise<void>
  /** Stop RTP for this consumer only. The producer keeps running and no other
   *  peer is affected — this is how a receiver gives back its own bandwidth. */
  pauseConsumer(consumerId: string): Promise<void>
  /**
   * Pause or resume MANY consumers in one request.
   *
   * lastN and the audio cap decide about the whole room at once, so the
   * per-consumer calls above meant ~32 round trips for a page flip and ~270
   * when the audio cap engages. `missing` names ids the server no longer has —
   * ordinary, since a consumer can close between deciding and arriving.
   */
  setConsumersPaused(consumerIds: string[], paused: boolean): Promise<{ missing?: string[] }>
  requestKeyframe(consumerId: string): Promise<void>
  /** Ask for a specific simulcast rung. Out-of-range values are clamped by
   *  mediasoup to what the producer actually has, so a caller can ask for the
   *  top layer without knowing how deep the ladder is. */
  setConsumerLayers(consumerId: string, spatialLayer: number, temporalLayer: number): Promise<void>
  pauseProducer(producerId: string): Promise<void>
  resumeProducer(producerId: string): Promise<void>
  closeProducer(producerId: string): Promise<void>
  /** Tell the server we're ready to receive: it stores our rtpCapabilities,
   *  creates consumers for every producer already in the room, and (re-)pushes
   *  `newConsumer` for each. Idempotent — also used as the reconnect re-sync. */
  readyToConsume(rtpCapabilities: unknown): Promise<void>
  /** Ensure the peer exists on the node + fetch caps (idempotent addPeer). */
  join(): Promise<void>
  /** Subscribe to a server push notification (e.g. "newConsumer"). */
  on(method: string, handler: (data: any) => void): () => void
  /** Register a callback fired on every socket (re)connect after the first. */
  onReconnect(handler: () => void): () => void
  /**
   * Register a callback fired once if another socket — this person's meeting
   * in another tab or window — takes this peer over. The socket has stopped.
   */
  onReplaced(handler: () => void): () => void
  /** Tear down the underlying socket. */
  close(): void
}

/** WS adapter — all control-plane operations over the persistent SfuSocket. */
export function createWsControl(socket: SfuSocket): SfuControl {
  const req = <T = any>(method: string, data?: unknown) => socket.request<T>(method, data)
  return {
    createTransport: (direction) => req('createWebRtcTransport', { direction }),
    connectTransport: async (transportId, dtlsParameters) => { await req('connectTransport', { transportId, dtlsParameters }) },
    produce: (transportId, kind, rtpParameters, appData) => req('produce', { transportId, kind, rtpParameters, appData }),
    restartIce: (transportId) => req('restartIce', { transportId }),
    consume: (producerId, rtpCapabilities) => req('consume', { producerId, rtpCapabilities }),
    resumeConsumer: async (consumerId) => { await req('resumeConsumer', { consumerId }) },
    pauseConsumer: async (consumerId) => { await req('pauseConsumer', { consumerId }) },
    setConsumersPaused: async (consumerIds, paused) => (
      await req('setConsumersPaused', { consumerIds, paused }) as { missing?: string[] }
    ),
    requestKeyframe: async (consumerId) => { await req('requestKeyframe', { consumerId }) },
    setConsumerLayers: async (consumerId, spatialLayer, temporalLayer) => { await req('setConsumerLayers', { consumerId, spatialLayer, temporalLayer }) },
    pauseProducer: async (producerId) => { await req('pauseProducer', { producerId }) },
    resumeProducer: async (producerId) => { await req('resumeProducer', { producerId }) },
    closeProducer: async (producerId) => { await req('closeProducer', { producerId }) },
    readyToConsume: async (rtpCapabilities) => { await req('readyToConsume', { rtpCapabilities }) },
    join: async () => { await req('join') },
    on: (method, handler) => socket.on(method, handler),
    onReconnect: (handler) => socket.onReconnect(handler),
    onReplaced: (handler) => socket.onReplaced(handler),
    close: () => socket.close(),
  }
}
